import { Injectable } from '@angular/core';
import { INotification, NotificationActionType } from '../../contract-management/services/notification.service';
import { ClauseLibraryService } from '../../contract-management/services/clause-library.service';
import { ToastService } from './toast.service';

/**
 * Handles notification actions by action type. When a notification has an actionType
 * in its event.data, handle() runs the corresponding logic (e.g. clause extraction retry).
 */
@Injectable({
    providedIn: 'root'
})
export class NotificationActionHandlerService {

    constructor(
        private clauseLibraryService: ClauseLibraryService,
        private toastService: ToastService
    ) {}

    /**
     * Handle a notification by running the logic for its action type.
     */
    handle(notification: INotification): void {
        const actionType = (notification.event?.data as { actionType?: string })?.actionType;

        if (!actionType) {
            console.warn('Notification has no actionType:', notification);
            return;
        }

        try {
            if (actionType === NotificationActionType.CLAUSE_EXTRACTION_RETRY) {
                this.handleClauseExtractionRetry(notification.event?.data as { clauseMetadataId?: string; bundleIds?: string[] });
            } else {
                console.warn(`No handler for notification action type: ${actionType}`);
            }
        } catch (error) {
            console.error(`Error handling notification action '${actionType}':`, error);
        }
    }

    private handleClauseExtractionRetry(payload: { clauseMetadataId?: string; bundleIds?: string[] }): void {
        const clauseMetadataId = payload?.clauseMetadataId;
        const bundleIds = payload?.bundleIds ?? [];

        if (!clauseMetadataId) {
            this.toastService.error('Invalid retry request: missing metadata ID');
            return;
        }

        this.clauseLibraryService.retryClauseExtraction(clauseMetadataId, bundleIds).subscribe({
            next: (response) => {
                if (response.success && response.data.success) {
                    this.toastService.success('Clause extraction retry submitted successfully');
                } else if (response.data.code === 'ALREADY_SUCCESS') {
                    this.toastService.info('Clause extraction has already completed successfully');
                } else if (response.data.code === 'ALREADY_RUNNING') {
                    this.toastService.info('Clause extraction is already in progress');
                } else {
                    this.toastService.error(response.message || 'Failed to retry clause extraction');
                }
            },
            error: (error) => {
                console.error('Error retrying clause extraction:', error);
                this.toastService.error(error?.error?.message || 'Failed to retry clause extraction');
            }
        });
    }

}
